import { Collapse, CollapseProps, Space } from 'antd';
import { observer } from 'mobx-react-lite';
import { useStore } from 'mobx-store-provider';
import * as React from 'react';
import { useToggle } from 'react-use';
import styled from 'styled-components';

import { Button } from '../../ButtonV2/ButtonV2';
import { colorsV2 } from '../../colors-v2';
import { Typography } from '../../DesignSystemV2/Typography';
import { FilterMobileStore } from '../store/filter-mobile.store';
import { FilterStore } from '../store/filter.store';
import { FilterItem } from './FilterItem';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

const { Panel } = Collapse;

export interface FilterGroupProps extends CollapseProps {
  groupId: string;
  model: typeof FilterStore | typeof FilterMobileStore;
}

const defaultCollapseProps: Partial<CollapseProps> = {
  defaultActiveKey: ['panel'],
  bordered: false,
  expandIconPosition: 'right',
  collapsible: 'disabled',
  ghost: true,
  expandIcon: ({ isActive }) => null,
};

export const FilterGroup = observer((props: FilterGroupProps) => {
  const { groupId, model, ...rest } = props;

  const [all, toggle] = useToggle(false);
  const filterStore = useStore(model);
  const group = filterStore.groups.find((gr) => gr.uuid === groupId);
  const { visibleOptions: options, name, selectedValue, setSelectedValue } = group;

  const defaultSortValue = React.useRef(selectedValue);

  const fallbackOption = group.options.find((opt) => opt.fallback);
  const hasShowMore = options.length > 5;
  const isSlice = hasShowMore && !all;
  const { isLoggedIn } = useGetAuthInfo();

  if (group.requireLoggedIn && !isLoggedIn) {
    return null;
  }

  React.useEffect(() => {
    if (!filterStore.isSearchFirst) {
      setSelectedValue(defaultSortValue.current);
    } else if (group.queryKey === 'sort') {
      setSelectedValue(undefined);
    }
  }, [group.queryKey, filterStore.isSearchFirst, setSelectedValue]);

  return (
    <StyledCollapse {...defaultCollapseProps} {...rest}>
      <Panel
        header={
          <Typography.Text
            variant="bold/14"
            desktopVariant="semi-bold/16"
            style={{ textTransform: 'uppercase' }}
          >
            {name}
            {hasShowMore ? ` (${options.length})` : ''}
          </Typography.Text>
        }
        key="panel"
        style={{ padding: 0 }}
      >
        <Space style={{ width: '100%' }} direction="vertical" size={[0, 12]}>
          {group.queryKey === 'sort' && filterStore.isSearchFirst && (
            <FilterItem
              key="most_relavace"
              checked={selectedValue === undefined}
              onClick={() => {
                if (selectedValue === undefined) {
                  setSelectedValue(fallbackOption?.value);
                } else {
                  setSelectedValue(undefined);
                }
                filterStore.update({ page: 1 });
              }}
            >
              Kết quả đúng nhất
            </FilterItem>
          )}
          {(isSlice ? options.slice(0, 5) : options).map(({ label, value }) => (
            <FilterItem
              key={value}
              level={(!filterStore.selectedCount && group.level ? group.level : null) as any}
              checked={selectedValue === value}
              onClick={() => {
                if (value === selectedValue) {
                  setSelectedValue(fallbackOption?.value);
                } else {
                  setSelectedValue(value);
                }
                filterStore.updateUrl();
                filterStore.update({ page: 1 });
              }}
            >
              {label}
            </FilterItem>
          ))}

          {hasShowMore && (
            <Button.Link style={{ color: colorsV2.blue100 }} onClick={() => toggle()}>
              {all ? 'Thu gọn' : 'Xem Tất Cả'}
            </Button.Link>
          )}
        </Space>
      </Panel>
    </StyledCollapse>
  );
});

const StyledCollapse = styled(Collapse)`
  &&& {
    .ant-collapse-header {
      padding: 4px 0;
      color: ${colorsV2.black100};
      cursor: text;

      svg {
        right: 0;
      }
    }

    .ant-collapse-content-box {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;
